/**
 * NarrativeManager — typed getters for all Starfall Courier narrative content.
 *
 * Wraps the static NARRATIVE_SCRIPT data with convenience accessors so callers
 * don't need to know the data shape. All content is bundled locally — zero
 * network calls.
 *
 * Phase 3 — World 1 Art Complete + Boss.
 * See ROADMAP.md Phase 3 and STORY.md.
 *
 * @module narrativeManager
 */

import { NARRATIVE_SCRIPT } from '../data/narrativeScript';
import { BELLE_NARRATIVE_SCRIPT } from '../data/belleNarrativeScript';
import { getActiveCharacterId } from '../data/activeCharacter';
import type {
  NarrativeLine,
  BossTaunt,
  WorldLoreLine,
  WorldCompleteText,
} from '../types/narrative';
import type { WorldId } from '../../constants/gameConstants';

/**
 * Return the active narrative script based on the selected character.
 * Belle has her own teen-slang variant; all others use the default script.
 *
 * @returns The NarrativeScript for the active character.
 */
function activeScript() {
  return getActiveCharacterId() === 'belle' ? BELLE_NARRATIVE_SCRIPT : NARRATIVE_SCRIPT;
}

/**
 * Return the intro cutscene lines (shown on first launch only).
 * @returns Array of narrative lines with optional speaker labels.
 */
export function getIntroLines(): NarrativeLine[] {
  return activeScript().intro.lines;
}

/**
 * Return the boss taunt for a given world.
 * @param worldId - The world whose boss taunt to retrieve.
 * @returns The boss taunt data including boss name and taunt text.
 */
export function getBossTaunt(worldId: WorldId): BossTaunt {
  return activeScript().bossTaunts[worldId];
}

/**
 * Return the hub-map lore line for a given world.
 * @param worldId - The world whose lore to retrieve.
 * @returns The lore data including world name, constellation, and text.
 */
export function getWorldLoreLine(worldId: WorldId): WorldLoreLine {
  return activeScript().worldLoreLines[worldId];
}

/**
 * Return the world-complete text shown after a boss is defeated.
 * @param worldId - The world that was just completed.
 * @returns The world complete data including congratulatory text.
 */
export function getWorldCompleteText(worldId: WorldId): WorldCompleteText {
  return activeScript().worldCompleteTexts[worldId];
}

/**
 * Return a random Nova bark for a given event type.
 * Each call picks a random line from the pool for variety.
 * @param event - The gameplay event that triggers the bark.
 * @returns A single bark string.
 */
export function getNovaBark(
  event: 'checkpointActivate' | 'shardCollected' | 'seedMilestone' | 'secretRoomFound' | 'lowHealth',
): string {
  const pool = activeScript().novaBarkSet[event];
  // DECISION: Math.random is fine here — bark variety, not security.
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Return Nova's boss-defeated bark for a specific world.
 * @param worldId - The world whose boss was just defeated.
 * @returns A single bark string.
 */
export function getBossDefeatedBark(worldId: WorldId): string {
  return activeScript().novaBarkSet.bossDefeated[worldId];
}

/**
 * Return the final ending cutscene lines and closing text.
 * @returns Object with lines array and closingText string.
 */
export function getEndingCutscene(): { lines: NarrativeLine[]; closingText: string } {
  return activeScript().ending;
}

const NarrativeManager = {
  getIntroLines,
  getBossTaunt,
  getWorldLoreLine,
  getWorldCompleteText,
  getNovaBark,
  getBossDefeatedBark,
  getEndingCutscene,
};
export default NarrativeManager;
